/**
 * pantsMap — which PANTS band a parent SKU's trousers are paid under.
 *
 * EVERY SET IS PAID TWICE, AND THE LEDGER ONLY KNOWS ABOUT ONE OF THEM. The
 * payslip pays a design band for the top AND a PANTS band for the trousers of
 * the same set: GUEVARRA's 16 sets on 7 Sep 2026 were 16 rows on ELITE bands
 * (₱1,780) plus 13 × ₱110 and 3 × ₱130 on PANTS (₱1,820). The records
 * sheet's QTY of 32 for that payslip is tops plus pants — `SUM(I17:J61)`
 * spans both blocks — and the ₱20 "additional charge" multiplies that
 * doubled count. A payroll that priced only the design band would pay
 * roughly half of what the workbook pays, and nothing on the payslip would
 * look wrong.
 *
 * BQC5 CARRIES THE PARENT SKU AND NOTHING ABOUT THE PANTS. Which trousers
 * SS028 has is knowledge that lived in the clerk's head. This file is that
 * knowledge written down: a map from design code to pants band, with WHERE
 * EACH ENTRY CAME FROM kept beside it, because some of it is read off real
 * payslips and some of it is inferred from the design's category, and a
 * person reviewing the map needs to know which is which.
 *
 *   evidenced   read directly off a 7 Sep 2026 payslip matched line-by-line
 *               against BQC5: 028/029/032/033/035 → ₱110 zipper; 031B → ₱100
 *               zip ply; 041/044/046 → ₱130 invisible/tapered (the jogger
 *               designs); ALTA and ELITE PRO categories → ₱150 ALTA.
 *   category    the design's BQC5 CATEGORY says ELITE / ALTA / ELITE PRO,
 *               and those categories were consistent on every evidenced
 *               design. An assumption, labelled as one on the Rate Card
 *               screen so it can be corrected there.
 *   set         chosen by a person on the Rate Card screen and stored in the
 *               module's settings list. Overrides both of the above.
 *
 * WHAT IS DELIBERATELY LEFT UNMAPPED. CLASSIC, PREMIUM and PRIMO designs have
 * no default: none of the four payslips carried one, and the pants ladder
 * for them runs from ₱60 to ₱100 — guessing would be inventing money. A
 * set on an unmapped design still pays its top band; the missing pants line
 * is reported on the run screen as work that needs a band choosing, exactly
 * the way an unpriced design is.
 *
 * Pure and I/O-free.
 */
import { RATE_CARD, normalizeDesign, type IRateRow } from "./rateCard";

/** The settings key the stored map lives under — see SubconPayrollService. */
export const PANTS_MAP_KEY = "pantsMap";

/** BQC5's own CATEGORY column, which is consistent per parent SKU. */
export type DesignCategory = "ELITE" | "ALTA" | "ELITE PRO" | "CLASSIC" | "PRIMO";

/**
 * Parent SKU → CATEGORY, from 19 months of the hub's BQC5 (33k rows). Every
 * code here carried one category on effectively every row; the handful of
 * rows where the CATEGORY cell held the SKU itself were ignored.
 */
export const DESIGN_CATEGORY: Readonly<Record<string, DesignCategory>> = {
  "028": "ELITE", "032": "ELITE", "041": "ELITE", "031B": "ELITE", "046": "ELITE", "029": "ELITE",
  "037": "ELITE", "033": "ELITE", "043": "ELITE", "044": "ELITE", "045": "ELITE", "031A": "ELITE",
  "035": "ELITE", "040": "ELITE", "038": "ELITE", "047": "ELITE", "042": "ELITE", "036": "ELITE",
  "048": "ELITE", "050": "ELITE", "034": "ELITE", "039": "ELITE", "030": "ELITE", "049": "ELITE",
  "031": "ELITE",
  "053": "ALTA", "056": "ALTA", "058": "ALTA", "057": "ALTA", "051": "ALTA", "063": "ALTA",
  "062": "ALTA", "064": "ALTA", "059": "ALTA", "054": "ALTA", "052": "ALTA", "055": "ALTA",
  "088": "ELITE PRO", "085": "ELITE PRO", "084": "ELITE PRO", "086": "ELITE PRO", "087": "ELITE PRO",
  "090": "ELITE PRO", "083": "ELITE PRO", "080": "ELITE PRO", "081": "ELITE PRO", "082": "ELITE PRO",
  "089": "ELITE PRO",
  "075": "PRIMO", "070": "PRIMO", "072": "PRIMO", "073": "PRIMO", "074": "PRIMO",
  "06C": "CLASSIC", "03B": "CLASSIC", "04": "CLASSIC", "01A": "CLASSIC", "06B": "CLASSIC",
  "018A": "CLASSIC", "03C": "CLASSIC", "023": "CLASSIC", "09E": "CLASSIC", "09": "CLASSIC",
  "04A": "CLASSIC", "01D": "CLASSIC", "015A": "CLASSIC", "09D": "CLASSIC", "015B": "CLASSIC",
  "010": "CLASSIC", "01B": "CLASSIC", "03J": "CLASSIC", "023A": "CLASSIC", "018": "CLASSIC",
  "01C": "CLASSIC", "022": "CLASSIC", "09B": "CLASSIC", "024": "CLASSIC", "04C": "CLASSIC",
  "025": "CLASSIC", "02": "CLASSIC", "016": "CLASSIC", "01I": "CLASSIC", "024A": "CLASSIC",
  "04D": "CLASSIC", "05C": "CLASSIC", "024C": "CLASSIC", "04H": "CLASSIC", "04B": "CLASSIC",
  "03D": "CLASSIC", "011": "CLASSIC", "05": "CLASSIC", "020A": "CLASSIC", "03A": "CLASSIC",
  "014": "CLASSIC", "021": "CLASSIC", "03H": "CLASSIC", "03": "CLASSIC", "06": "CLASSIC",
  "012": "CLASSIC", "03I": "CLASSIC", "03E": "CLASSIC", "020": "CLASSIC", "013": "CLASSIC",
  "019B": "CLASSIC", "04I": "CLASSIC", "027": "CLASSIC", "03F": "CLASSIC", "019": "CLASSIC",
};

/** The pants band a category defaults to. Absent means "no default". */
export const CATEGORY_PANTS: Readonly<Partial<Record<DesignCategory, string>>> = {
  ELITE: "pants-6",        // ₱110 CARGO 6-POCKET (2-in) w/ ZIPPER or DOUBLE WELT
  ALTA: "pants-9",         // ₱150 ALTA CARGO/PANTS STRETCHFINE
  "ELITE PRO": "pants-9",  // ₱150 — SS083 and SS087 were paid ALTA pants on 7 Sep 2026
};

/** Designs read straight off a matched payslip. These beat the category rule. */
export const EVIDENCED_PANTS: Readonly<Record<string, string>> = {
  "028": "pants-6", "029": "pants-6", "032": "pants-6", "033": "pants-6", "035": "pants-6",
  "031B": "pants-5",                                    // ARINGO: 4 × SS031B → 4 × ZIP PLY ₱100
  "041": "pants-8", "044": "pants-8", "046": "pants-8", // GUEVARRA: 041×2 + 046×1 → 3 × ₱130
  "057": "pants-9", "062": "pants-9", "063": "pants-9", "058": "pants-9", "083": "pants-9", "087": "pants-9",
};

export type PantsBasis = "evidenced" | "category" | "set";

export interface IPantsEntry {
  design: string;
  category?: DesignCategory;
  /** Rate-card key of the pants band, or "" for "this design has no pants line". */
  pantsRateKey: string;
  basis: PantsBasis;
}

/** What the settings list holds: design → pants rateKey ("" = none). */
export type StoredPantsMap = Readonly<Record<string, string>>;

/** Same three spellings rateForDesign tolerates — "29", "029", "029 " are
 *  one design in the grid and must be one design here. */
function lookup<T>(map: Readonly<Record<string, T>>, code: string): T | undefined {
  return map[code] ?? map[code.replace(/^0+/, "")] ?? map[code.padStart(3, "0")];
}

/** The category BQC5 files a design under, if we have seen it. */
export function categoryOf(design: string | undefined | null): DesignCategory | undefined {
  const code = normalizeDesign(design);
  return code ? lookup(DESIGN_CATEGORY, code) : undefined;
}

/**
 * The band a design's pants are paid under, or null.
 *
 * Stored choice first (a person decided), then a payslip we matched, then the
 * category rule. An explicit "" in the stored map means somebody decided this
 * design has no pants line, and that is honoured — it is how a tops-only
 * design is expressed.
 */
/**
 * Designs priced whole, with no pants line: the gowns and coats on the rate
 * card's GOWNS & COATS rows. Their codes are the SKU prefix itself.
 */
export const NO_PANTS: ReadonlySet<string> = new Set([
  "LG", "NU", "WU01", "WU02", "WU03", "WU04", "WU05", "DC01", "DC02", "DC03", "DC04", "DC05", "DC05L", "DC05S", "DC06", "DC06J",
]);

export function pantsEntryFor(
  design: string | undefined | null,
  stored: StoredPantsMap = {},
): IPantsEntry | null {
  const code = normalizeDesign(design);
  if (!code) return null;
  const category = lookup(DESIGN_CATEGORY, code);
  const set = lookup(stored, code);
  if (set !== undefined) return { design: code, category, pantsRateKey: set, basis: "set" };
  // Gowns and coats are priced whole; "" is the same answer a person gives
  // by mapping a design to "no pants line", so the run records no gap.
  if (NO_PANTS.has(code)) return { design: code, category, pantsRateKey: "", basis: "evidenced" };
  const ev = lookup(EVIDENCED_PANTS, code);
  if (ev) return { design: code, category, pantsRateKey: ev, basis: "evidenced" };
  const byCat = category ? CATEGORY_PANTS[category] : undefined;
  if (byCat) return { design: code, category, pantsRateKey: byCat, basis: "category" };
  return null;
}

/** The pants rate-card row for a design, or null when unmapped, mapped to
 *  "none", or mapped to a band the card no longer carries. */
export function pantsRowFor(
  design: string | undefined | null,
  stored: StoredPantsMap = {},
  card: readonly IRateRow[] = RATE_CARD,
): IRateRow | null {
  const e = pantsEntryFor(design, stored);
  if (!e || !e.pantsRateKey) return null;
  const row = card.find(r => r.rateKey === e.pantsRateKey);
  return row && row.group === "PANTS" && row.rate !== null ? row : null;
}

/**
 * Every design worth showing on the Rate Card screen's pants table: the ones
 * the card prices, the ones BQC5 has categorised, and the ones somebody has
 * stored a choice for — each with its current resolution, in card order
 * first and then alphabetically.
 */
export function pantsTable(
  stored: StoredPantsMap = {},
  card: readonly IRateRow[] = RATE_CARD,
): (IPantsEntry | { design: string; category?: DesignCategory; pantsRateKey: ""; basis: "unmapped" })[] {
  const seen = new Set<string>();
  const designs: string[] = [];
  for (const r of card) for (const d of r.designs) if (!seen.has(d)) { seen.add(d); designs.push(d); }
  for (const d of [...Object.keys(DESIGN_CATEGORY), ...Object.keys(stored)].sort()) {
    if (!seen.has(d)) { seen.add(d); designs.push(d); }
  }
  return designs.map(d =>
    pantsEntryFor(d, stored) ?? { design: d, category: lookup(DESIGN_CATEGORY, d), pantsRateKey: "" as const, basis: "unmapped" as const });
}

/** Parse what the settings list holds. Anything that is not a flat object of
 *  string → string is treated as "no stored choices" rather than crashing a
 *  payroll run on a hand-edited cell. */
export function parseStoredPantsMap(text: string | null | undefined): StoredPantsMap {
  if (!text) return {};
  try {
    const v: unknown = JSON.parse(text);
    if (!v || typeof v !== "object" || Array.isArray(v)) return {};
    const out: Record<string, string> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) {
      const code = normalizeDesign(k);
      if (code && typeof val === "string") out[code] = val;
    }
    return out;
  } catch {
    return {};
  }
}
